import { useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { HAND_OFF_HINT, HAND_OFF_TITLE, TO_OKTHX } from "@/shared/taskRowCopy.ts";
import { MAX_CONTEXT_CHARS } from "@/shared/userTaskRow.ts";
import type { UserTaskContext } from "@/types/userTasks.ts";

/**
 * The step between deciding to hand a task over and handing it over.
 *
 * Okthx works from the task, and the note is the part of it the reader wrote
 * — so the moment of handing it over is the moment they know what is missing
 * from it. Asking afterwards is asking too late: the work has started, and a
 * correction then is a correction to something already under way.
 *
 * The task's own note, not a message that travels with the hand-off. There is
 * one place where what the reader knows about a task lives, and a second one
 * written at the door would be read once and then lost behind it.
 *
 * Mounted only while open, so the field starts from the saved note every
 * time: a draft abandoned with Cancel was a decision, not a save.
 */
export const HandOffDialog = ({
  context,
  subject,
  saving,
  onClose,
  onHandOff,
}: {
  context: UserTaskContext | null;
  /** What the task is, so it is clear which one is being handed over. */
  subject: string;
  saving: boolean;
  onClose: () => void;
  /** The note as it stands when they send, which is saved only if it changed. */
  onHandOff: (note: string) => void;
}) => {
  const [note, setNote] = useState(context?.text ?? "");
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{HAND_OFF_TITLE}</DialogTitle>
          <DialogDescription>{subject}</DialogDescription>
        </DialogHeader>
        <Textarea
          autoFocus
          aria-label={HAND_OFF_HINT}
          placeholder={HAND_OFF_HINT}
          className="min-h-24"
          maxLength={MAX_CONTEXT_CHARS}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-statsig-name="tasks-handoff-cancel"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            data-statsig-name="tasks-handoff-send"
            disabled={saving}
            onClick={() => onHandOff(note)}
          >
            {TO_OKTHX}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
